/**
 * PaymentAgentReadinessStatus — komponent statusu Global Readiness Gate.
 *
 * Sprint 2 / E5. Konsumuje `usePaymentAgentReadiness` hook + `paymentAgentReadinessMessages`
 * lookup. Nie zawiera własnej logiki decyzyjnej — jest wyłącznie prezentacyjny.
 *
 * Stany wizualne:
 *   🟢 ready               — backend `ready=true`
 *   🟠 preparing           — trwa poprawny proces (KSeF import, sync, analiza)
 *   🟠 automation_waiting  — automatyzacja włączona + preparing
 *   🔴 not_ready           — partial/failed/interrupted/stale/brak danych/etc.
 *   ⚪ no_new_data         — tylko CURRENT_GENERATION_ALREADY_ANALYZED
 *   🔄 loading             — pierwsze pobranie
 *
 * Design: kompaktowa belka (nie panel diagnostyczny). Detale po hoverze/kliknięciu
 * — pokaż wszystkie blockery, jeżeli backend zwrócił więcej niż jeden.
 */
import React, { useState } from "react";
import {
  CheckCircle2,
  Clock,
  AlertTriangle,
  Info,
  Loader2,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

import { cn } from "../../lib/utils";
import {
  computeReadinessVisualState,
  describeBlockingReason,
} from "../../lib/paymentAgentReadinessMessages";

const STATE_STYLES = {
  ready: {
    icon: CheckCircle2,
    iconClass: "text-emerald-600",
    borderClass: "border-emerald-200",
    bgClass: "bg-emerald-50",
    textClass: "text-emerald-800",
    label: "Gotowe do analizy",
  },
  preparing: {
    icon: Clock,
    iconClass: "text-amber-600",
    borderClass: "border-amber-200",
    bgClass: "bg-amber-50",
    textClass: "text-amber-800",
    label: "W przygotowaniu",
  },
  automation_waiting: {
    icon: Clock,
    iconClass: "text-amber-600",
    borderClass: "border-amber-200",
    bgClass: "bg-amber-50",
    textClass: "text-amber-800",
    label: "Automatyzacja włączona — oczekiwanie na komplet danych",
  },
  not_ready: {
    icon: AlertTriangle,
    iconClass: "text-red-600",
    borderClass: "border-red-200",
    bgClass: "bg-red-50",
    textClass: "text-red-800",
    label: "Nie gotowe",
  },
  no_new_data: {
    icon: Info,
    iconClass: "text-gray-500",
    borderClass: "border-gray-200",
    bgClass: "bg-gray-50",
    textClass: "text-gray-700",
    label: "Brak nowych danych do analizy",
  },
  loading: {
    icon: Loader2,
    iconClass: "text-gray-400 animate-spin",
    borderClass: "border-gray-200",
    bgClass: "bg-white",
    textClass: "text-gray-500",
    label: "Sprawdzanie gotowości…",
  },
};

/**
 * @param {object} props
 * @param {object|null} props.readiness — kontrakt z `usePaymentAgentReadiness`
 * @param {boolean} props.loading
 * @param {string|null} props.error
 * @param {string} [props.className]
 */
export function PaymentAgentReadinessStatus({
  readiness,
  loading,
  error,
  className,
}) {
  const [expanded, setExpanded] = useState(false);

  // Loading / error → prosty komunikat.
  if (loading && !readiness) {
    const style = STATE_STYLES.loading;
    const Icon = style.icon;
    return (
      <div
        className={cn(
          "flex items-center gap-2 px-3 py-2 rounded-lg border text-sm",
          style.borderClass, style.bgClass, style.textClass, className,
        )}
        data-testid="payment-readiness-loading"
      >
        <Icon className={cn("w-4 h-4 shrink-0", style.iconClass)} />
        <span>{style.label}</span>
      </div>
    );
  }
  if (error && !readiness) {
    const style = STATE_STYLES.not_ready;
    const Icon = style.icon;
    return (
      <div
        className={cn(
          "flex items-center gap-2 px-3 py-2 rounded-lg border text-sm",
          style.borderClass, style.bgClass, style.textClass, className,
        )}
        data-testid="payment-readiness-error"
      >
        <Icon className={cn("w-4 h-4 shrink-0", style.iconClass)} />
        <span>Nie udało się sprawdzić gotowości.</span>
      </div>
    );
  }
  if (!readiness) return null;

  const { state, primary } = computeReadinessVisualState(readiness);
  const style = STATE_STYLES[state] || STATE_STYLES.not_ready;
  const Icon = style.icon;

  const primaryMessage = primary?.message || style.label;
  const allReasons = Array.isArray(readiness.blocking_reasons)
    ? readiness.blocking_reasons.map(describeBlockingReason)
    : [];
  const showExpandable = allReasons.length > 1;

  return (
    <div
      className={cn(
        "px-3 py-2 rounded-lg border text-sm",
        style.borderClass, style.bgClass, className,
      )}
      data-testid="payment-readiness-status"
      data-state={state}
    >
      <div className={cn("flex items-start gap-2", style.textClass)}>
        <Icon className={cn("w-4 h-4 shrink-0 mt-0.5", style.iconClass)} />
        <div className="flex-1 min-w-0">
          <div className="font-medium">{style.label}</div>
          {state !== "ready" && primaryMessage && (
            <div className="text-xs mt-0.5 opacity-90" data-testid="payment-readiness-primary-reason">
              {primaryMessage}
            </div>
          )}
        </div>
        {showExpandable && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className={cn(
              "text-xs underline hover:no-underline shrink-0",
              style.textClass,
            )}
            data-testid="payment-readiness-toggle-details"
          >
            <span className="hidden sm:inline">
              {expanded ? "Zwiń" : `Więcej (${allReasons.length})`}
            </span>
            {expanded ? (
              <ChevronUp className="w-4 h-4 sm:hidden" />
            ) : (
              <ChevronDown className="w-4 h-4 sm:hidden" />
            )}
          </button>
        )}
      </div>
      {expanded && showExpandable && (
        <ul
          className={cn("mt-2 pl-6 space-y-1 text-xs", style.textClass)}
          data-testid="payment-readiness-details-list"
        >
          {allReasons.map((r) => (
            <li key={r.code} className="flex items-start gap-1.5">
              <span className="opacity-60 shrink-0">•</span>
              <span>{r.message}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default PaymentAgentReadinessStatus;
